import type { CSSProperties, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useI18n } from '../state/i18n.js';

// ── 법무 페이지 — 개인 운영자(사업자 아님) 기준, 영문 우선 ──────────────────
// CloudBTL 은 현재 개인이 운영한다. 법인/사업자등록번호가 없으므로 그 필드는 두지 않는다
// (있는 척 지어내지 않는다). 수집 항목 서술은 실제 구현(Room.tsx 계측, api events.service
// 의 IP·User-Agent 기록, access.ts 의 email 캡처)과 일치해야 한다 — 계측이 바뀌면 여기도 고친다.
// 보관 기간·연락처는 운영자가 정한 실제 값이며, 규모가 커지면 정식 법률 검토를 받는다.

type LegalDoc = 'terms' | 'privacy';

const EFFECTIVE_DATE = '2026-08-11';
const CONTACT_EMAIL = 'hi@cloudbtl.com';

const PAGE_STYLE: CSSProperties = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '32px 20px 96px',
  lineHeight: 1.7,
};

/** 운영자 정보 — 개인 운영자. 사업자등록번호 없음(개인). */
function OperatorInfo({ lang }: { lang: 'ko' | 'en' }): JSX.Element {
  return (
    <ul>
      <li>
        {lang === 'ko' ? '서비스명: ' : 'Service: '}
        CloudBTL (cloudbtl.com)
      </li>
      <li>
        {lang === 'ko'
          ? '운영: 개인 운영자 (별도 사업자 등록 없음)'
          : 'Operated by: an individual operator (not a registered business)'}
      </li>
      <li>
        {lang === 'ko' ? '문의: ' : 'Contact: '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </li>
    </ul>
  );
}

function PrivacyEn(): JSX.Element {
  return (
    <>
      <h1>Privacy Policy</h1>
      <h2>1. Who we are</h2>
      <p>
        CloudBTL (the &ldquo;Service&rdquo;) lets senders upload documents, share them via links,
        and see how recipients read them. The Service is run by an individual operator, not a
        registered company. This policy explains what the Service collects, why, and how it is
        handled.
      </p>
      <OperatorInfo lang="en" />
      <p>
        For any privacy request or question, contact <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>2. What we collect</h2>
      <h3>2-1. Account holders (document senders)</h3>
      <ul>
        <li>Email address, name, password (stored hashed) or Google sign-in information</li>
        <li>Uploaded document files and their metadata (filename, size, format)</li>
      </ul>
      <h3>2-2. Website visitors</h3>
      <ul>
        <li>A visitor identifier (a random browser-stored value) and a session identifier</li>
        <li>Visited paths, visit and exit times, referrer, and clicked button or link targets</li>
        <li>IP address and browser information (User-Agent), recorded during server request handling</li>
      </ul>
      <h3>2-3. Document recipients (viewers)</h3>
      <p>
        From the moment a shared link is opened, the following is collected and made available to
        the document sender:
      </p>
      <ul>
        <li>IP address, browser and device information (User-Agent), screen size</li>
        <li>Time of viewing and the page URL visited</li>
        <li>
          A visitor identifier stored in the browser (localStorage) — a random value used to
          recognize return visits; deleting browser site data removes it
        </li>
        <li>
          Time spent on each page, how far the document was read (scroll depth and per-section dwell
          time), and section-level reading events
        </li>
        <li>
          Gate information on protected links — for organization links, the email verified via
          Google sign-in; for email-gated links, the email the viewer enters (not verified)
        </li>
      </ul>

      <h2>3. Why we collect it (purposes)</h2>
      <ul>
        <li>
          Providing reading analytics to the document sender (who read, when, and how much) — the
          core function of the Service
        </li>
        <li>Link access control (passcode, organization, email gates) and abuse prevention</li>
        <li>Aggregating homepage visits and conversion flows to improve the Service</li>
      </ul>

      <h2>4. Retention and deletion</h2>
      <ul>
        <li>
          <strong>Account data</strong> (email, name, documents, metadata): kept while your account
          is active, and deleted when you delete your account or request deletion. Residual copies in
          encrypted backups are removed within 30 days.
        </li>
        <li>
          <strong>Viewing and analytics events</strong> (IP, User-Agent, visitor identifier, reading
          events): retained for up to 12 months, then deleted on a rolling basis.
        </li>
      </ul>

      <h2>5. Who we share it with (processors)</h2>
      <ul>
        <li>Google Cloud Platform — server hosting, file and data storage</li>
        <li>Cloudflare — content delivery (CDN), security and traffic handling</li>
        <li>PayPal — payment processing for paid plans (we do not receive card details)</li>
      </ul>
      <p>
        Depending on where these processors operate, your data may be processed outside your country.
        We do not sell personal data.
      </p>

      <h2>6. Your rights</h2>
      <ul>
        <li>
          You may request access, correction, deletion, or restriction of processing of your data at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </li>
        <li>You can delete your account and its data from your account settings.</li>
        <li>The visitor identifier can be removed by clearing your browser&rsquo;s site data.</li>
      </ul>

      <h2>7. Changes</h2>
      <p>
        Changes to this policy will be posted on this page. Effective date: {EFFECTIVE_DATE}.
      </p>
    </>
  );
}

function PrivacyKo(): JSX.Element {
  return (
    <>
      <h1>개인정보 처리방침</h1>
      <h2>1. 운영 주체</h2>
      <p>
        CloudBTL(이하 &ldquo;서비스&rdquo;)은 문서를 업로드해 공유 링크로 전달하고, 수신자의 열람
        활동을 분석해 발신자에게 제공하는 서비스입니다. 본 서비스는 사업자가 아닌 개인이 운영합니다.
        본 방침은 서비스가 어떤 정보를 왜 수집하고 어떻게 다루는지 설명합니다.
      </p>
      <OperatorInfo lang="ko" />
      <p>
        개인정보 관련 문의·요청: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>

      <h2>2. 수집하는 정보</h2>
      <h3>2-1. 회원(문서 발신자)</h3>
      <ul>
        <li>이메일 주소, 이름, 비밀번호(암호화 저장) 또는 Google 로그인 정보</li>
        <li>업로드한 문서 파일과 그 메타데이터(파일명·크기·형식)</li>
      </ul>
      <h3>2-2. 홈페이지 방문자</h3>
      <ul>
        <li>방문자 식별자(브라우저에 저장되는 무작위 값), 세션 식별자</li>
        <li>방문 경로, 방문·이탈 시각, 유입 페이지(Referer), 클릭 대상</li>
        <li>IP 주소와 브라우저 정보(User-Agent) — 서버 요청 처리 과정에서 기록</li>
      </ul>
      <h3>2-3. 문서 수신자(열람자)</h3>
      <p>공유 링크로 문서를 여는 순간부터 아래 정보가 수집되어 발신자에게 제공됩니다.</p>
      <ul>
        <li>IP 주소, 브라우저·기기 정보(User-Agent), 화면 크기</li>
        <li>열람 시각, 접속 페이지 주소</li>
        <li>브라우저 저장소의 방문자 식별자(무작위 값 — 브라우저 데이터 삭제 시 제거)</li>
        <li>페이지별 체류 시간, 읽은 범위(스크롤 깊이·구간별 체류), 섹션 수준 열람 이벤트</li>
        <li>
          보호된 링크 통과 정보 — 조직 링크는 Google 로그인으로 검증된 이메일, 이메일 게이트 링크는
          열람자가 입력한 이메일(검증 없음)
        </li>
      </ul>

      <h2>3. 이용 목적</h2>
      <ul>
        <li>발신자에게 열람 분석 제공(누가·언제·얼마나 읽었는지) — 서비스 핵심 기능</li>
        <li>링크 접근 제어(패스코드·조직·이메일 게이트) 및 부정사용 방지</li>
        <li>방문·전환 흐름 집계 및 서비스 개선</li>
      </ul>

      <h2>4. 보관 및 파기</h2>
      <ul>
        <li>
          <strong>계정 데이터</strong>(이메일·이름·문서·메타데이터): 계정 유지 동안 보관, 계정 삭제
          또는 삭제 요청 시 파기. 암호화 백업의 잔여본은 30일 내 제거.
        </li>
        <li>
          <strong>열람·분석 이벤트</strong>(IP·User-Agent·방문자 식별자·열람 이벤트): 최대 12개월
          보관 후 롤링 파기.
        </li>
      </ul>

      <h2>5. 처리 위탁 및 국외 이전</h2>
      <ul>
        <li>Google Cloud Platform — 서버 운영, 파일·데이터 저장</li>
        <li>Cloudflare — 콘텐츠 전송(CDN), 보안·트래픽 처리</li>
        <li>PayPal — 유료 플랜 결제 처리(카드 정보는 당사가 수신하지 않음)</li>
      </ul>
      <p>수탁사 처리 위치에 따라 정보가 국외에서 처리될 수 있습니다. 개인정보를 판매하지 않습니다.</p>

      <h2>6. 이용자의 권리</h2>
      <ul>
        <li>
          본인 정보의 열람·정정·삭제·처리정지를 <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          로 요구할 수 있습니다.
        </li>
        <li>계정 설정에서 계정과 데이터를 직접 삭제할 수 있습니다.</li>
        <li>방문자 식별자는 브라우저 사이트 데이터 삭제로 제거할 수 있습니다.</li>
      </ul>

      <h2>7. 고지</h2>
      <p>본 방침 변경 시 이 페이지에 게시합니다. 시행일: {EFFECTIVE_DATE}</p>
    </>
  );
}

function TermsEn(): JSX.Element {
  return (
    <>
      <h1>Terms of Service</h1>
      <h2>1. Purpose</h2>
      <p>
        These terms govern the use of CloudBTL (the &ldquo;Service&rdquo;), run by an individual
        operator. The Service lets users upload documents, issues share links, and provides the
        sender with analytics on how link recipients read the documents.
      </p>

      <h2>2. Accounts</h2>
      <p>
        Some features require signing up with an email or Google account. You are responsible for
        keeping your account information accurate and your credentials secure.
      </p>

      <h2>3. User content</h2>
      <p>
        You retain the rights to, and responsibility for, the documents you upload. Do not upload
        content that infringes others&rsquo; rights or violates applicable law. We may remove content
        or suspend accounts that violate these terms.
      </p>

      <h2>4. Sender responsibilities regarding viewer data</h2>
      <p>
        The Service collects link recipients&rsquo; viewing information (time of viewing, time per
        page, read depth, IP and device information, etc. — see the Privacy Policy) and provides it
        to the sender. Where applicable law requires, the sender is responsible for informing
        recipients of this collection.
      </p>

      <h2>5. Paid plans</h2>
      <p>
        Paid plans are billed monthly in advance through PayPal and renew automatically each month
        until cancelled. You can cancel anytime from your PayPal account or by contacting{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>; cancellation stops future renewals
        and takes effect at the end of the current billing period. Free trials do not auto-charge.
      </p>

      <h2>6. Prohibited conduct</h2>
      <ul>
        <li>Interfering with normal operation (automated bulk requests, attempts to bypass security)</li>
        <li>Impersonating others or using share links for deceptive purposes</li>
        <li>Distributing malware or illegal content</li>
      </ul>

      <h2>7. Changes to the Service</h2>
      <p>
        The Service may change or discontinue features; material changes will be announced in
        advance where practical.
      </p>

      <h2>8. Disclaimer and limitation of liability</h2>
      <p>
        The Service is provided &ldquo;as is&rdquo; without warranties of any kind. To the extent
        permitted by law, the operator is not liable for indirect or consequential damages arising
        from use of the Service.
      </p>

      <h2>9. Governing law</h2>
      <p>These terms are governed by the laws of the Republic of Korea.</p>

      <h2>10. Operator</h2>
      <OperatorInfo lang="en" />
      <p>Effective date: {EFFECTIVE_DATE}</p>
    </>
  );
}

function TermsKo(): JSX.Element {
  return (
    <>
      <h1>이용약관</h1>
      <h2>1. 목적</h2>
      <p>
        본 약관은 개인이 운영하는 CloudBTL(이하 &ldquo;서비스&rdquo;)의 이용 조건을 정합니다.
        서비스는 이용자가 문서를 업로드하면 공유 링크를 발행하고, 수신자의 열람 활동 분석을
        발신자에게 제공합니다.
      </p>

      <h2>2. 계정</h2>
      <p>
        일부 기능은 이메일 또는 Google 계정 가입이 필요합니다. 계정 정보를 정확히 유지하고 자격
        증명을 안전하게 관리할 책임은 이용자에게 있습니다.
      </p>

      <h2>3. 이용자 콘텐츠</h2>
      <p>
        업로드하는 문서의 권리·책임은 이용자에게 있습니다. 타인의 권리를 침해하거나 법령을 위반하는
        콘텐츠를 업로드해서는 안 되며, 위반 시 콘텐츠 삭제·계정 정지될 수 있습니다.
      </p>

      <h2>4. 열람 정보 수집에 관한 발신자의 책무</h2>
      <p>
        서비스는 수신자의 열람 정보(열람 시각·페이지별 체류·읽은 범위·IP·기기 정보 등 — 상세는
        개인정보 처리방침)를 수집해 발신자에게 제공합니다. 관련 법령이 요구하는 경우 발신자가
        수신자에게 이를 알릴 책임을 부담합니다.
      </p>

      <h2>5. 유료 플랜</h2>
      <p>
        유료 플랜은 PayPal을 통해 매월 선불로 청구되며 취소 전까지 매월 자동 갱신됩니다. PayPal
        계정 또는 <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>로 언제든 취소할 수 있고,
        취소 시 다음 갱신부터 중단되며 현재 결제 주기 종료 시 반영됩니다. 무료 체험은 자동 청구되지
        않습니다.
      </p>

      <h2>6. 금지 행위</h2>
      <ul>
        <li>정상 운영 방해(자동화된 대량 요청, 보안 우회 시도 등)</li>
        <li>타인 사칭 또는 기만 목적의 공유 링크 사용</li>
        <li>악성코드·불법 콘텐츠 배포</li>
      </ul>

      <h2>7. 서비스 변경·중단</h2>
      <p>서비스는 기능을 변경·중단할 수 있으며, 중대한 변경은 가능한 한 사전 공지합니다.</p>

      <h2>8. 보증의 부인 및 책임 제한</h2>
      <p>
        서비스는 &ldquo;있는 그대로&rdquo; 제공되며 어떠한 보증도 하지 않습니다. 법이 허용하는
        범위에서 운영자는 서비스 이용으로 인한 간접·결과적 손해에 책임지지 않습니다.
      </p>

      <h2>9. 준거법</h2>
      <p>본 약관은 대한민국 법률에 따릅니다.</p>

      <h2>10. 운영자 정보</h2>
      <OperatorInfo lang="ko" />
      <p>시행일: {EFFECTIVE_DATE}</p>
    </>
  );
}

export function LegalPage({ doc }: { doc?: LegalDoc }): JSX.Element {
  const { lang } = useI18n();
  const params = useParams<{ doc?: string }>();
  const resolved: LegalDoc = doc ?? (params.doc === 'privacy' ? 'privacy' : 'terms');

  useDocumentMeta({
    title:
      resolved === 'privacy'
        ? lang === 'ko'
          ? '개인정보 처리방침'
          : 'Privacy Policy'
        : lang === 'ko'
          ? '이용약관'
          : 'Terms of Service',
  });

  return (
    <Shell>
      <article style={PAGE_STYLE} data-testid={`legal-${resolved}`}>
        {resolved === 'privacy' ? (
          lang === 'ko' ? (
            <PrivacyKo />
          ) : (
            <PrivacyEn />
          )
        ) : lang === 'ko' ? (
          <TermsKo />
        ) : (
          <TermsEn />
        )}
      </article>
    </Shell>
  );
}
